<template>
  <div class="row">
    <div class="col-xl-12">
      <div class="kt-portlet kt-portlet--mobile">
        <!-- Header -->
        <div class="kt-portlet__head kt-portlet__head--lg">
          <div class="kt-portlet__head-label">
            <span class="kt-portlet__head-icon">
              <i :class="pageIcon"></i>
            </span>
            <h3 class="kt-portlet__head-title">
              {{ pageTitle }}
            </h3>
          </div>
          <div class="kt-portlet__head-toolbar">
            <button type="button" class="btn btn-secondary" @click="goBack">
              <i class="la la-close"></i> ย้อนกลับ
            </button>
          </div>
        </div>

        <div class="kt-portlet__body">
          <!-- Loading Indicator -->
          <div v-if="isLoading && (isEditMode || isViewMode)" class="text-center py-5">
            <div class="kt-spinner kt-spinner--lg kt-spinner--brand"></div>
            <div class="mt-3">กำลังโหลดข้อมูล...</div>
          </div>

          <!-- Main Content (hide while loading in Edit/View mode) -->
          <div v-show="!isLoading || (!isEditMode && !isViewMode)">
          <!-- Search Section (only show in Edit/View mode) -->
          <div v-if="isEditMode || isViewMode">
            <div class="row">
              <!--
                CR-BudgetReport ข้อ 2 (สเปก 24/09/2026)
                เอาช่องพิมพ์ keyword "IO / GL" เดิมออก แทนด้วย multi-select 4 ช่องแบบ cascading
                ไม่มีช่อง CCTR เพราะเข้าหน้านี้ผ่านแว่นขยาย/ดินสอ ระบบรู้อยู่แล้ว
              -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>IO :</label>
                  <treeselect
                    v-model="searchIOCodes"
                    :multiple="true"
                    :options="ioTreeOptions"
                    :clear-on-select="true"
                    placeholder="ทั้งหมด (เลือกได้หลายรายการ)"
                    @input="onFilterChanged"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>GL :</label>
                  <treeselect
                    v-model="searchGLCodes"
                    :multiple="true"
                    :options="glTreeOptions"
                    :clear-on-select="true"
                    placeholder="ทั้งหมด (เลือกได้หลายรายการ)"
                    @input="onFilterChanged"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>แผนก :</label>
                  <treeselect
                    v-model="searchOrgCodes"
                    :multiple="true"
                    :options="orgFilterTreeOptions"
                    :clear-on-select="true"
                    placeholder="ทั้งหมด (เลือกได้หลายแผนก)"
                    @input="onFilterChanged"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>หมวดหมู่ :</label>
                  <treeselect
                    v-model="searchGLTypeNames"
                    :multiple="true"
                    :options="glTypeTreeOptions"
                    :clear-on-select="true"
                    placeholder="ทั้งหมด (เลือกได้หลายรายการ)"
                    @input="onFilterChanged"
                  />
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-6">
                <button type="button" class="btn btn-primary" @click="searchBudgetPlan">
                  ค้นหา
                </button>
                <button type="button" class="btn btn-secondary" @click="clearSearch">
                  ล้างค่าทั้งหมด
                </button>
              </div>
              <div class="col-6 text-right">
                <button type="button" class="btn btn-success" @click="exportBudgetHistory" :disabled="isExporting">
                  <i :class="isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
                  {{ isExporting ? 'กำลัง Export...' : 'Export ข้อมูลงบประมาณ' }}
                </button>
              </div>
            </div>
            <hr />
          </div>

          <!-- Level 1: CCTR Card -->
          <div class="card mb-3 border-primary">
            <div
              class="card-header bg-primary text-white"
              style="cursor: pointer;"
              @click="toggleLevel1"
            >
              <h5 class="mb-0">
                <i :class="level1Collapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-down'"></i>
                ข้อมูล CCTR<span v-if="formData.cctrCode"> - [{{ formData.cctrCode }}] {{ formData.cctrName }}</span>
              </h5>
            </div>
            <div class="card-body" v-show="!level1Collapsed">
              <!-- CCTR Fields -->
              <div class="row">
                <div class="col-md-3">
                  <div class="form-group">
                    <label>รหัส CCTR : <span class="text-danger">*</span></label>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'readonly-field': isCCTRCodeReadOnly() }"
                      v-model="formData.cctrCode"
                      placeholder="รหัส CCTR"
                      maxlength="20"
                      @blur="onCCTRBlur"
                      :readonly="isCCTRCodeReadOnly()"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>ชื่อ CCTR : <span class="text-danger">*</span></label>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'readonly-field': isViewMode }"
                      v-model="formData.cctrName"
                      placeholder="ชื่อ CCTR"
                      :readonly="isViewMode"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>ปีงบประมาณ : <span class="text-danger">*</span></label>
                    <select2
                      :css-style="'width: 100%;'"
                      :css-class="'form-control m-select2'"
                      :id="'budgetYearSelect'"
                      :name="'budgetYearSelect'"
                      :select2-options="yearOptions"
                      v-model="formData.year"
                      ref="budgetYearSelect2"
                      :disabled="isViewMode || isEditMode"
                    ></select2>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>วันที่บันทึกล่าสุด :</label>
                    <input type="text" class="form-control" :value="currentDate" readonly />
                  </div>
                </div>
              </div>

              <!-- Add IO Button -->
              <div v-if="!isViewMode" class="row mb-3">
                <div class="col-12 text-right">
                  <button
                    type="button"
                    class="btn btn-warning text-white btn-sm"
                    @click="addIO"
                  >
                    <i class="fas fa-plus"></i> เพิ่ม IO
                  </button>
                </div>
              </div>

              <!-- Level 2: IO Cards -->
              <div v-for="(io, ioIndex) in formData.ioList" :key="'io-' + ioIndex" class="card mb-3 border-info ml-3">
                <div
                  class="card-header bg-info text-white d-flex justify-content-between align-items-center"
                  style="cursor: pointer;"
                >
                  <h6 class="mb-0" @click="toggleIO(ioIndex)">
                    <i :class="io.collapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-down'"></i>
                    ข้อมูล IO<span v-if="io.ioCode"> - [{{ io.ioCode }}] {{ io.ioName }}</span>
                  </h6>
                  <button
                    v-if="!isViewMode && canDeleteIO(io) && formData.ioList.length > 1"
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click.stop="removeIO(ioIndex)"
                    title="ลบ IO"
                  >
                    <i class="la la-trash"></i>
                  </button>
                </div>
                <div class="card-body" v-show="!io.collapsed">
                  <!-- IO Fields -->
                  <div class="row">
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>รหัส IO : <span class="text-danger">*</span></label>
                        <input
                          type="text"
                          class="form-control"
                          :class="{ 'readonly-field': isIOCodeReadOnly(ioIndex) }"
                          v-model="io.ioCode"
                          placeholder="รหัส IO"
                          maxlength="20"
                          @blur="onIOBlur(ioIndex)"
                          :readonly="isIOCodeReadOnly(ioIndex)"
                        />
                      </div>
                    </div>
                    <div class="col-md-5">
                      <div class="form-group">
                        <label>ชื่อ IO :</label>
                        <input
                          type="text"
                          class="form-control"
                          :class="{ 'readonly-field': isViewMode }"
                          v-model="io.ioName"
                          placeholder="ชื่อ IO"
                          :readonly="isViewMode"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="summary-box bg-light border rounded p-2">
                        <div class="d-flex justify-content-between">
                          <span>IO รวมงบประมาณ :</span>
                          <span class="font-weight-bold">{{ formatNumber(calculateIOBudgetTotal(io)) }}</span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>IO รวมงบประมาณคงเหลือ :</span>
                          <span class="font-weight-bold text-primary">{{ formatNumber(calculateIOTotal(io, 'remainingAmount')) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- IO Action Buttons -->
                  <div v-if="!isViewMode" class="row mb-3">
                    <div class="col-12 text-right">
                      <button
                        type="button"
                        class="btn btn-warning text-white btn-sm"
                        @click="addGL(ioIndex)"
                      >
                        <i class="fas fa-plus"></i> เพิ่ม GL
                      </button>
                    </div>
                  </div>

                  <!-- Level 3: GL Cards -->
                  <div v-for="(gl, glIndex) in io.glList" :key="'gl-' + ioIndex + '-' + glIndex" class="card mb-3 border-success ml-3">
                    <div
                      class="card-header bg-success text-white d-flex justify-content-between align-items-center"
                      style="cursor: pointer;"
                    >
                      <h6 class="mb-0" @click="toggleGL(ioIndex, glIndex)">
                        <i :class="gl.collapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-down'"></i>
                        ข้อมูล GL<span v-if="gl.glCode"> - [{{ gl.glCode }}] {{ gl.glName }}</span>
                      </h6>
                      <button
                        v-if="!isViewMode && canDeleteGL(gl) && io.glList.length > 1"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click.stop="removeGL(ioIndex, glIndex)"
                        title="ลบ GL"
                      >
                        <i class="la la-trash"></i>
                      </button>
                    </div>
                    <div class="card-body" v-show="!gl.collapsed">
                      <!-- GL Fields -->
                      <div class="row">
                        <div class="col-md-3">
                          <div class="form-group">
                            <label>รหัส GL : <span class="text-danger">*</span></label>
                            <input
                              type="text"
                              class="form-control"
                              :class="{ 'readonly-field': isGLCodeReadOnly(ioIndex, glIndex) }"
                              v-model="gl.glCode"
                              placeholder="รหัส GL"
                              maxlength="20"
                              @blur="onGLBlur(ioIndex, glIndex)"
                              :readonly="isGLCodeReadOnly(ioIndex, glIndex)"
                            />
                          </div>
                        </div>
                        <!--
                          ISS-35 (7 ต.ค. 2026 · QA BUG-14) กล่องสรุปของ GL ต้องกว้างเท่ากล่องของ IO (col-md-4)
                          ISS-40 (8 ต.ค. 2026 · QA BUG-13) "หมวดหมู่" ย้ายขึ้นมาแถวเดียวกับ "ชื่อ GL"
                          สัดส่วน 3 (รหัส) / 3 (ชื่อ) / 2 (หมวดหมู่) / 4 (กล่องสรุป) — รหัส GL ยังตรงกับรหัส IO
                        -->
                        <div class="col-md-3">
                          <div class="form-group">
                            <label>ชื่อ GL :</label>
                            <input
                              type="text"
                              class="form-control"
                              :class="{ 'readonly-field': isViewMode }"
                              v-model="gl.glName"
                              placeholder="ชื่อ GL"
                              :readonly="isViewMode"
                            />
                          </div>
                        </div>
                        <!--
                          CR-BudgetReport ข้อ 3 — ค่าผูกกับ "แถวงบ" ไม่ใช่กับ GL master
                          GL เดียวกันคนละ IO เป็นคนละหมวดได้ (Q-B6)
                          ISS-39 (QA BUG-12 · BA เคาะ) เลือกจากหมวดหมู่ใน DB + พิมพ์ชื่อใหม่ได้ (backend สร้าง master ให้ · EnsureGLType)
                          ISS-45 เปลี่ยนจาก <datalist> เป็น select2 แบบ tags — ดู GLTypeSelect.vue
                        -->
                        <div class="col-md-2">
                          <div class="form-group">
                            <label>หมวดหมู่ :</label>
                            <gl-type-select
                              v-model="gl.glTypeName"
                              :options="glTypeNameOptions"
                              :disabled="isViewMode"
                              placeholder="เช่น Asset, A&P"
                            ></gl-type-select>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <div class="summary-box bg-light border rounded p-2">
                            <div class="d-flex justify-content-between">
                              <span>GL รวมงบประมาณ :</span>
                              <span class="font-weight-bold">{{ formatNumber(calculateGLBudgetTotal(gl)) }}</span>
                            </div>
                            <div class="d-flex justify-content-between">
                              <span>GL รวมงบประมาณคงเหลือ :</span>
                              <span class="font-weight-bold text-success">{{ formatNumber(calculateGLTotal(gl, 'remainingAmount')) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Level 4: Org Table -->
                      <label class="font-weight-bold">แผนก :</label>
                      <div class="table-responsive">
                        <table class="table table-bordered table-sm">
                          <thead class="thead-light">
                            <tr>
                              <th width="5%" class="text-center">ลำดับ</th>
                              <th width="25%" class="text-center">ชื่อแผนก <span class="text-danger">*</span></th>
                              <th width="15%" class="text-center">งบประมาณตั้งต้น <span class="text-danger">*</span></th>
                              <th width="10%" class="text-center">ใช้ไป</th>
                              <th width="10%" class="text-center">รวมปรับเพิ่มงบประมาณ</th>
                              <th width="10%" class="text-center">รวมปรับลดงบประมาณ</th>
                              <th width="15%" class="text-center">คงเหลืองบประมาณ</th>
                              <th width="10%" class="text-center">คำสั่ง</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(org, orgIndex) in gl.orgList" :key="'org-' + (org._uid || orgIndex)">
                              <td class="text-center">{{ orgIndex + 1 }}</td>
                              <td>
                                <treeselect
                                  v-model="org.orgCode"
                                  :options="orgTreeOptions"
                                  :clearable="true"
                                  :disabled="isViewMode || org.isReadOnly"
                                  placeholder="กรุณาเลือกแผนก"
                                  @input="onOrgChange(ioIndex, glIndex, orgIndex)"
                                />
                              </td>
                              <td>
                                <span v-if="isViewMode || org.isReadOnly" class="d-block text-right">
                                  {{ formatNumber(org.budgetAmount) }}
                                </span>
                                <input
                                  v-else
                                  type="text"
                                  class="form-control form-control-sm text-right"
                                  :value="org._isEditing ? org.budgetAmount : formatNumber(org.budgetAmount)"
                                  @focus="onBudgetFocus($event, org)"
                                  @blur="onBudgetBlur($event, ioIndex, glIndex, orgIndex)"
                                  @keydown="preventNegativeInput($event)"
                                />
                              </td>
                              <td class="text-right">{{ formatNumber(org.usedAmount) }}</td>
                              <td class="text-right">{{ formatNumber((org.addedAmount || 0) + (org.sendbackAmount || 0)) }}</td>
                              <td class="text-right">{{ formatNumber(org.reducedAmount) }}</td>
                              <td class="text-right font-weight-bold">{{ formatNumber(org.remainingAmount) }}</td>
                              <td class="text-center">
                                <a
                                  v-if="!isViewMode && !org.isReadOnly"
                                  href="javascript:;"
                                  class="btn btn-sm btn-clean btn-icon btn-icon-md"
                                  @click="removeOrgRow(ioIndex, glIndex, orgIndex)"
                                  title="ลบรายการ"
                                >
                                  <i class="la la-trash"></i>
                                </a>
                                <router-link
                                  v-if="org.isReadOnly && org.budgetPlanId"
                                  :to="'/BudgetPlan/Detail/' + org.budgetPlanId"
                                  class="btn btn-sm btn-clean btn-icon btn-icon-md"
                                  title="ดูรายละเอียด"
                                >
                                  <i class="fa fa-search"></i>
                                </router-link>
                                <span v-if="org.isReadOnly && !org.budgetPlanId" class="badge badge-secondary">
                                  <i class="fas fa-lock"></i> ใช้แล้ว
                                </span>
                              </td>
                            </tr>
                            <tr v-if="gl.orgList.length === 0">
                              <td colspan="8" class="text-center text-muted">
                                ยังไม่มีข้อมูลแผนก คลิก "เพิ่มรายการ" เพื่อเพิ่ม
                              </td>
                            </tr>
                          </tbody>
                          <tfoot class="bg-light">
                            <tr>
                              <td colspan="2" class="text-right font-weight-bold">รวม:</td>
                              <td class="text-right font-weight-bold">{{ formatNumber(calculateGLTotal(gl, 'budgetAmount')) }}</td>
                              <td class="text-right font-weight-bold">{{ formatNumber(calculateGLTotal(gl, 'usedAmount')) }}</td>
                              <td class="text-right font-weight-bold">{{ formatNumber(calculateGLTotal(gl, 'addedAmount') + calculateGLTotal(gl, 'sendbackAmount')) }}</td>
                              <td class="text-right font-weight-bold">{{ formatNumber(calculateGLTotal(gl, 'reducedAmount')) }}</td>
                              <td class="text-right font-weight-bold">{{ formatNumber(calculateGLTotal(gl, 'remainingAmount')) }}</td>
                              <td></td>
                            </tr>
                            <tr>
                              <td colspan="2" class="text-danger text-right font-weight-bold border-0 p-0 pt-1" style="font-size: 0.8rem;">* ไม่รวมภาษีมูลค่าเพิ่ม 7 %</td>
                              <td colspan="6" class="border-0"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <!-- GL Action Buttons -->
                      <div v-if="!isViewMode" class="mt-2">
                        <button
                          type="button"
                          class="btn btn-primary btn-sm"
                          @click="addOrgRow(ioIndex, glIndex)"
                        >
                          <i class="fas fa-plus"></i> เพิ่มรายการ
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="row">
            <div class="col-12">
              <hr />
              <button type="button" class="btn btn-secondary" @click="goBack">
                <i class="la la-close"></i> ย้อนกลับ
              </button>
              <button
                v-if="!isViewMode"
                type="button"
                class="btn btn-primary"
                @click="saveBudgetPlan"
                :disabled="isSaving"
              >
                <i class="la la-save"></i> {{ isSaving ? 'กำลังบันทึก...' : 'บันทึก' }}
              </button>
            </div>
          </div>
          </div><!-- End of Main Content wrapper -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import router from "../../router";
import select2 from "../../Shared/Select2";
import GlTypeSelect from "./GLTypeSelect";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

require("sweetalert2");

export default {
  components: {
    select2,
    Treeselect,
    GlTypeSelect,
  },
  data: function () {
    var currentYear = new Date().getFullYear();
    var now = new Date();
    var currentDate = String(now.getDate()).padStart(2, '0') + '/' +
                      String(now.getMonth() + 1).padStart(2, '0') + '/' +
                      now.getFullYear();

    return {
      // Search
      searchKeyword: "",          // คงไว้เพื่อ backward compat — หน้าจอไม่ใช้แล้ว
      searchOrgCodes: [],

      // ---- CR-BudgetReport ข้อ 2: filter แบบ cascading ----
      searchIOCodes: [],
      searchGLCodes: [],
      searchGLTypeNames: [],
      ioTreeOptions: [],
      glTreeOptions: [],
      glTypeTreeOptions: [],
      orgFilterTreeOptions: [],
      // กัน race: ถ้าผู้ใช้เปลี่ยน filter เร็วกว่า response กลับมา ให้ทิ้งของเก่า (SQ-02)
      filterOptionsSeq: 0,

      // Form Data
      formData: {
        year: currentYear.toString(),
        cctrCode: "",
        cctrName: "",
        isFilteredView: false, // Issue 47: true = กำลังดูผลลัพธ์จาก filter → ข้าม delete logic ตอน save
        ioList: [
          {
            ioCode: "",
            ioName: "",
            collapsed: false,
            isFromDb: false,
            glList: [
              {
                glCode: "",
                glName: "",
                glTypeName: "",
                collapsed: false,
                isFromDb: false,
                orgList: [],
              },
            ],
          },
        ],
      },

      // UI State
      level1Collapsed: false,
      isSaving: false,
      isLoading: false,
      isExporting: false,

      // Options
      yearOptions: {
        placeholder: "เลือกปีงบประมาณ",
        allowClear: false,
        data: [
          { id: currentYear.toString(), text: currentYear.toString() },
          { id: (currentYear + 1).toString(), text: (currentYear + 1).toString() },
        ],
      },
      orgOptions: {
        placeholder: "กรุณาเลือกแผนก",
        allowClear: true,
        data: [],
      },
      orgTreeOptions: [],
      // ISS-39: หมวดหมู่ที่มีอยู่ ใช้เป็นตัวเลือกของช่อง "หมวดหมู่"
      glTypeNameOptions: [],

      // Current Date
      currentDate: currentDate,

      // UID Counter for unique keys
      orgUidCounter: 0,
    };
  },
  computed: {
    isEditMode: function () {
      return !!(this.$route.params.year && this.$route.params.cctrCode) && !this.isViewMode;
    },
    isViewMode: function () {
      return this.$route.path.includes('/View/');
    },
    isReadOnly: function () {
      // ทั้ง Edit mode ที่มี data จาก DB และ View mode ควรเป็น readonly สำหรับบาง field
      // แต่ View mode จะเป็น readonly ทั้งหมด
      return this.isViewMode;
    },
    pageTitle: function () {
      if (this.isViewMode) {
        return 'ดูรายละเอียดแผนงบประมาณ';
      } else if (this.isEditMode) {
        return 'แก้ไขแผนงบประมาณ';
      } else {
        return 'เพิ่มแผนงบประมาณ';
      }
    },
    pageIcon: function () {
      if (this.isViewMode) {
        return 'fas fa-eye';
      } else if (this.isEditMode) {
        return 'fas fa-edit';
      } else {
        return 'fas fa-plus-circle';
      }
    },
  },
  mounted: async function () {
    // IMPORTANT: Must await dropdown data first before loading edit data
    // This prevents race condition where edit data arrives before dropdown options
    // causing Select2 dropdowns to not bind values properly
    await this.loadDropdownData();

    // Check if in edit mode or view mode - load existing data
    if (this.isEditMode || this.isViewMode) {
      await this.loadEditData();

      // CR-BudgetReport ข้อ 2: เตรียมตัวเลือกของ filter ทั้ง 4 ช่อง
      await this.reloadFilterOptions();
    }
  },
  methods: {
    goBack: function () {
      this.$router.push("/BudgetPlan/List");
    },

    // Check if any org in the entire CCTR has been used
    isCCTRCodeReadOnly: function () {
      if (this.isViewMode) return true;
      if (!this.isEditMode) return false;

      // Check if any org in any GL in any IO has been used
      for (var i = 0; i < this.formData.ioList.length; i++) {
        var io = this.formData.ioList[i];
        for (var j = 0; j < io.glList.length; j++) {
          var gl = io.glList[j];
          for (var k = 0; k < gl.orgList.length; k++) {
            var org = gl.orgList[k];
            if (org.isReadOnly) {
              return true;
            }
          }
        }
      }
      return false;
    },

    // Check if any org under this IO has been used
    isIOCodeReadOnly: function (ioIndex) {
      if (this.isViewMode) return true;
      if (!this.isEditMode) return false;

      var io = this.formData.ioList[ioIndex];
      if (!io) return false;

      for (var j = 0; j < io.glList.length; j++) {
        var gl = io.glList[j];
        for (var k = 0; k < gl.orgList.length; k++) {
          var org = gl.orgList[k];
          if (org.isReadOnly) {
            return true;
          }
        }
      }
      return false;
    },

    // Check if any org under this GL has been used
    isGLCodeReadOnly: function (ioIndex, glIndex) {
      if (this.isViewMode) return true;
      if (!this.isEditMode) return false;

      var io = this.formData.ioList[ioIndex];
      if (!io) return false;

      var gl = io.glList[glIndex];
      if (!gl) return false;

      for (var k = 0; k < gl.orgList.length; k++) {
        var org = gl.orgList[k];
        if (org.isReadOnly) {
          return true;
        }
      }
      return false;
    },

    // Load Dropdown Data
    loadDropdownData: async function () {
      var self = this;
      try {
        self.isLoading = true;
        var response = await axios.get("/Api/BudgetPlan/Create/Dropdown", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
        });

        if (response.data && response.data.success) {
          // Set org options
          var orgData = (response.data.data.orgCodes || []).map(function (item) {
            return { id: item.value, text: item.text };
          });
          self.orgOptions = {
            placeholder: "กรุณาเลือกแผนก",
            allowClear: true,
            data: orgData,
          };

          // ISS-39: หมวดหมู่ที่มีอยู่แล้ว
          self.glTypeNameOptions = response.data.data.glTypeNames || [];
          // Treeselect options format: [{id, label}]
          self.orgTreeOptions = (response.data.data.orgCodes || []).map(function (item) {
            return { id: item.value, label: item.text };
          });

          // orgTreeOptions is already set above, treeselect will use it via v-model binding
        }

        self.isLoading = false;
      } catch (error) {
        self.isLoading = false;
        console.error("Error loading dropdown data:", error);
      }
    },

    // Load Edit Data
    loadEditData: async function () {
      var self = this;
      var year = self.$route.params.year;
      var cctrCode = self.$route.params.cctrCode;

      if (!year || !cctrCode) return;

      try {
        self.isLoading = true;
        KTApp.blockPage({ opacity: 0.05, message: "กำลังโหลดข้อมูล..." });

        var response = await axios.get("/Api/BudgetPlan/Edit/" + year + "/" + cctrCode, {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
        });

        KTApp.unblockPage();
        self.isLoading = false;

        if (response.data && response.data.success && response.data.data) {
          var data = response.data.data;

          if (!data.found) {
            Swal.fire("ไม่พบข้อมูล", "ไม่พบข้อมูลแผนงบประมาณที่ต้องการแก้ไข", "error");
            self.$router.push("/BudgetPlan/List");
            return;
          }

          // Populate form data
          self.formData.year = data.year.toString();
          self.formData.cctrCode = data.cctrCode;
          self.formData.cctrName = data.cctrName;
          self.formData.isFilteredView = false; // Issue 47: โหลดข้อมูลเต็ม → อนุญาตให้ลบได้

          // FR-006: ใช้ lastModifiedDate จาก API (BudgetPlan_CostCenter.UpdatedDate ?? CreatedDate)
          if (data.lastModifiedDate) {
            var date = new Date(data.lastModifiedDate);
            self.currentDate = String(date.getDate()).padStart(2, '0') + '/' +
                              String(date.getMonth() + 1).padStart(2, '0') + '/' +
                              date.getFullYear();
          }

          if (data.ioList && data.ioList.length > 0) {
            self.formData.ioList = data.ioList.map(function (io) {
              return {
                ioCode: io.ioCode,
                ioName: io.ioName || "",
                collapsed: false,
                isFromDb: io.isFromDb !== false,
                glList: (io.glList || []).map(function (gl) {
                  return {
                    glCode: gl.glCode,
                    glName: gl.glName || "",
                    glTypeName: gl.glTypeName || "",
                    collapsed: false,
                    isFromDb: gl.isFromDb !== false,
                    orgList: (gl.orgList || []).map(function (org) {
                      self.orgUidCounter++;
                      return {
                        _uid: self.orgUidCounter,
                        orgCode: org.orgCode,
                        orgName: org.orgName || "",
                        budgetAmount: org.budgetAmount || 0,
                        usedAmount: org.usedAmount || 0,
                        addedAmount: org.addedAmount || 0,
                        sendbackAmount: org.sendbackAmount || 0,
                        reducedAmount: org.reducedAmount || 0,
                        remainingAmount: org.remainingAmount || 0,
                        budgetTotal: org.budgetTotal || 0,
                        isReadOnly: org.isReadOnly === true,
                        budgetPlanId: org.budgetPlanId,
                        // Store original codes for tracking changes
                        originalCCTRCode: data.cctrCode,
                        originalIOCode: io.ioCode,
                        originalGLCode: gl.glCode,
                      };
                    }),
                  };
                }),
              };
            });
          } else {
            // Cascade filter: ถ้า API ส่ง ioList ว่างกลับมา (ไม่มี Org ที่ user มีสิทธิ์)
            // ให้เคลียร์ default IO/GL ว่างออก เพื่อไม่แสดง Level 2,3 ที่ไม่มีข้อมูล
            self.formData.ioList = [];
          }
        } else {
          Swal.fire("เกิดข้อผิดพลาด", response.data.message || "ไม่สามารถโหลดข้อมูลได้", "error");
          self.$router.push("/BudgetPlan/List");
        }
      } catch (error) {
        KTApp.unblockPage();
        self.isLoading = false;
        console.error("Error loading edit data:", error);
        Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถโหลดข้อมูลได้", "error");
        self.$router.push("/BudgetPlan/List");
      }
    },

    // Toggle Functions
    toggleLevel1: function () {
      this.level1Collapsed = !this.level1Collapsed;
    },
    toggleIO: function (ioIndex) {
      this.formData.ioList[ioIndex].collapsed = !this.formData.ioList[ioIndex].collapsed;
    },
    toggleGL: function (ioIndex, glIndex) {
      this.formData.ioList[ioIndex].glList[glIndex].collapsed =
        !this.formData.ioList[ioIndex].glList[glIndex].collapsed;
    },

    // Add Functions
    addIO: function () {
      this.formData.ioList.unshift({
        ioCode: "",
        ioName: "",
        collapsed: false,
        isFromDb: false,
        glList: [
          {
            glCode: "",
            glName: "",
            glTypeName: "",
            collapsed: false,
            isFromDb: false,
            orgList: [],
          },
        ],
      });
    },
    addGL: function (ioIndex) {
      this.formData.ioList[ioIndex].glList.unshift({
        glCode: "",
        glName: "",
        glTypeName: "",
        collapsed: false,
        isFromDb: false,
        orgList: [],
      });
    },
    addOrgRow: function (ioIndex, glIndex) {
      this.orgUidCounter++;
      this.formData.ioList[ioIndex].glList[glIndex].orgList.push({
        _uid: this.orgUidCounter,
        orgCode: null,
        orgName: "",
        budgetAmount: 0,
        usedAmount: 0,
        addedAmount: 0,
        sendbackAmount: 0,
        reducedAmount: 0,
        remainingAmount: 0,
        budgetTotal: 0,
        isReadOnly: false,
        budgetPlanId: null,
      });
    },

    // Remove Functions
    removeIO: function (ioIndex) {
      var self = this;
      Swal.fire({
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบ IO นี้หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ใช่, ลบ",
        cancelButtonText: "ยกเลิก",
      }).then(function (result) {
        if (result.value) {
          self.formData.ioList.splice(ioIndex, 1);
        }
      });
    },
    removeGL: function (ioIndex, glIndex) {
      var self = this;
      Swal.fire({
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบ GL นี้หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ใช่, ลบ",
        cancelButtonText: "ยกเลิก",
      }).then(function (result) {
        if (result.value) {
          self.formData.ioList[ioIndex].glList.splice(glIndex, 1);
        }
      });
    },
    removeOrgRow: function (ioIndex, glIndex, orgIndex) {
      var self = this;
      var org = this.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex];
      if (org.budgetPlanId && org.usedAmount > 0) {
        Swal.fire("ไม่สามารถลบได้", "รายการนี้มีการใช้งบประมาณแล้ว", "error");
        return;
      }
      Swal.fire({
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบรายการนี้หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ใช่, ลบ",
        cancelButtonText: "ยกเลิก",
      }).then(function (result) {
        if (result.value) {
          self.formData.ioList[ioIndex].glList[glIndex].orgList.splice(orgIndex, 1);
        }
      });
    },

    // Blur Events - Auto load data
    onCCTRBlur: async function () {
      var self = this;
      var code = self.formData.cctrCode;
      var year = self.formData.year;

      if (!code || !year) return;

      try {
        var response = await axios.get("/Api/BudgetPlan/Create/Lookup", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
          params: {
            type: "CCTR",
            code: code,
            year: year,
          },
        });

        if (response.data && response.data.success && response.data.data) {
          if (response.data.data.found) {
            self.formData.cctrName = response.data.data.name;
          }
        }
      } catch (error) {
        console.error("Error looking up CCTR:", error);
      }
    },
    onIOBlur: async function (ioIndex) {
      var self = this;
      var io = self.formData.ioList[ioIndex];
      var code = io.ioCode;

      if (!code) return;

      try {
        var response = await axios.get("/Api/BudgetPlan/Create/Lookup", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
          params: {
            type: "IO",
            code: code,
          },
        });

        if (response.data && response.data.success && response.data.data) {
          if (response.data.data.found) {
            io.ioName = response.data.data.name;
          }
        }
      } catch (error) {
        console.error("Error looking up IO:", error);
      }
    },
    onGLBlur: async function (ioIndex, glIndex) {
      var self = this;
      var gl = self.formData.ioList[ioIndex].glList[glIndex];
      var code = gl.glCode;

      if (!code) return;

      // Lookup GL name
      try {
        var response = await axios.get("/Api/BudgetPlan/Create/Lookup", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
          params: {
            type: "GL",
            code: code,
          },
        });

        if (response.data && response.data.success && response.data.data) {
          if (response.data.data.found) {
            gl.glName = response.data.data.name;
          }
        }
      } catch (error) {
        console.error("Error looking up GL:", error);
      }

      // Also load existing budget data if all fields are filled
      // But NOT if GL already has data from DB (edit mode) - to preserve original codes
      var year = self.formData.year;
      var cctrCode = self.formData.cctrCode;
      var ioCode = self.formData.ioList[ioIndex].ioCode;
      var glCode = gl.glCode;

      if (year && cctrCode && ioCode && glCode && !gl.isFromDb) {
        await self.loadBudgetData(ioIndex, glIndex);
      }
    },
    onOrgChange: async function (ioIndex, glIndex, orgIndex) {
      var self = this;
      // When org is selected, check for existing budget
      var year = this.formData.year;
      var cctrCode = this.formData.cctrCode;
      var ioCode = this.formData.ioList[ioIndex].ioCode;
      var glCode = this.formData.ioList[ioIndex].glList[glIndex].glCode;
      var orgCode = this.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex].orgCode;

      // Check for duplicate org in the same GL
      if (orgCode) {
        var orgList = this.formData.ioList[ioIndex].glList[glIndex].orgList;
        var isDuplicate = orgList.some(function (org, idx) {
          return idx !== orgIndex && org.orgCode === orgCode;
        });

        if (isDuplicate) {
          Swal.fire("ไม่สามารถเพิ่มได้", "แผนกนี้มีอยู่ในรายการแล้ว กรุณาเลือกแผนกอื่น", "warning");
          // Clear the selection
          self.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex].orgCode = null;
          self.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex].orgName = "";
          // Reset Select2 dropdown
          self.$nextTick(function () {
            var selectId = "#orgSelect-" + ioIndex + "-" + glIndex + "-" + orgIndex;
            $(selectId).val(null).trigger("change");
          });
          return;
        }
      }

      if (year && cctrCode && ioCode && glCode && orgCode) {
        await this.loadSingleBudgetData(ioIndex, glIndex, orgIndex);
      }
    },

    // Load Budget Data
    loadBudgetData: async function (ioIndex, glIndex) {
      var self = this;
      try {
        var response = await axios.get("/Api/BudgetPlan/Create/BudgetData", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
          params: {
            year: self.formData.year,
            cctrCode: self.formData.cctrCode,
            ioCode: self.formData.ioList[ioIndex].ioCode,
            glCode: self.formData.ioList[ioIndex].glList[glIndex].glCode,
          },
        });

        if (response.data && response.data.success && response.data.data) {
          var budgetList = response.data.data;
          if (budgetList.length > 0) {
            // Store current codes as original codes for tracking changes
            var currentCCTRCode = self.formData.cctrCode;
            var currentIOCode = self.formData.ioList[ioIndex].ioCode;
            var currentGLCode = self.formData.ioList[ioIndex].glList[glIndex].glCode;

            // Replace orgList with existing data
            self.formData.ioList[ioIndex].glList[glIndex].orgList = budgetList.map(function (item) {
              self.orgUidCounter++;
              return {
                _uid: self.orgUidCounter,
                orgCode: item.orgCode,
                orgName: item.orgName,
                budgetAmount: item.budgetAmount || 0,
                usedAmount: item.usedAmount || 0,
                addedAmount: item.addedAmount || 0,
                sendbackAmount: item.sendbackAmount || 0,
                reducedAmount: item.reducedAmount || 0,
                remainingAmount: item.remainingAmount || 0,
                budgetTotal: item.budgetTotal || 0,
                isReadOnly: item.usedAmount > 0 || item.addedAmount > 0 || item.sendbackAmount > 0 || item.reducedAmount > 0,
                budgetPlanId: item.budgetPlanId,
                // Store original codes for tracking changes
                originalCCTRCode: currentCCTRCode,
                originalIOCode: currentIOCode,
                originalGLCode: currentGLCode,
              };
            });
          }
        }
      } catch (error) {
        console.error("Error loading budget data:", error);
      }
    },

    loadSingleBudgetData: async function (ioIndex, glIndex, orgIndex) {
      var self = this;
      var org = self.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex];

      try {
        var response = await axios.get("/Api/BudgetPlan/Create/BudgetData", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
          params: {
            year: self.formData.year,
            cctrCode: self.formData.cctrCode,
            ioCode: self.formData.ioList[ioIndex].ioCode,
            glCode: self.formData.ioList[ioIndex].glList[glIndex].glCode,
            orgCode: org.orgCode,
          },
        });

        if (response.data && response.data.success && response.data.data && response.data.data.length > 0) {
          var item = response.data.data[0];
          org.budgetAmount = item.budgetAmount || 0;
          org.usedAmount = item.usedAmount || 0;
          org.addedAmount = item.addedAmount || 0;
          org.sendbackAmount = item.sendbackAmount || 0;
          org.reducedAmount = item.reducedAmount || 0;
          org.remainingAmount = item.remainingAmount || 0;
          org.budgetTotal = item.budgetTotal || 0;
          org.isReadOnly = item.usedAmount > 0 || item.addedAmount > 0 || item.sendbackAmount > 0 || item.reducedAmount > 0;
          org.budgetPlanId = item.budgetPlanId;
          // Store original codes for tracking changes
          org.originalCCTRCode = self.formData.cctrCode;
          org.originalIOCode = self.formData.ioList[ioIndex].ioCode;
          org.originalGLCode = self.formData.ioList[ioIndex].glList[glIndex].glCode;
        }
      } catch (error) {
        console.error("Error loading single budget data:", error);
      }
    },

    // Calculate
    calculateRemaining: function (ioIndex, glIndex, orgIndex) {
      var org = this.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex];
      org.remainingAmount = (org.budgetAmount || 0) + (org.addedAmount || 0) + (org.sendbackAmount || 0) - (org.usedAmount || 0) - (org.reducedAmount || 0);
    },
    validateAndCalculateRemaining: function (ioIndex, glIndex, orgIndex) {
      var org = this.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex];
      // Ensure positive value
      if (org.budgetAmount === null || org.budgetAmount === undefined || org.budgetAmount === '' || isNaN(org.budgetAmount) || org.budgetAmount < 0) {
        org.budgetAmount = 0;
      }
      this.calculateRemaining(ioIndex, glIndex, orgIndex);
    },
    onBudgetFocus: function (event, org) {
      // Mark as editing and show raw number (without comma)
      this.$set(org, '_isEditing', true);
      // Set value to raw number
      event.target.value = org.budgetAmount || 0;
      // Select all text for easy editing
      event.target.select();
    },
    onBudgetBlur: function (event, ioIndex, glIndex, orgIndex) {
      var org = this.formData.ioList[ioIndex].glList[glIndex].orgList[orgIndex];
      // Parse the value (remove commas if user typed them)
      var rawValue = event.target.value.replace(/,/g, '');
      var numValue = parseFloat(rawValue);

      // Validate and set value
      if (isNaN(numValue) || numValue < 0) {
        numValue = 0;
      }
      org.budgetAmount = numValue;

      // Mark as not editing (will show formatted number)
      this.$set(org, '_isEditing', false);

      // Calculate remaining
      this.calculateRemaining(ioIndex, glIndex, orgIndex);
    },
    preventNegativeInput: function (event) {
      // Allow: backspace, delete, tab, escape, enter, home, end, arrows
      var allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (allowedKeys.indexOf(event.key) !== -1) return;
      // Allow Ctrl/Cmd+A, C, V, X
      if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].indexOf(event.key.toLowerCase()) !== -1) return;
      // Allow digits and decimal point only
      if (/^[0-9.]$/.test(event.key)) {
        // Prevent multiple decimal points
        if (event.key === '.' && event.target.value.indexOf('.') !== -1) {
          event.preventDefault();
        }
        return;
      }
      // Block everything else (letters, minus, symbols, etc.)
      event.preventDefault();
    },
    calculateGLTotal: function (gl, field) {
      var total = 0;
      gl.orgList.forEach(function (org) {
        total += org[field] || 0;
      });
      return total;
    },
    calculateIOTotal: function (io, field) {
      var self = this;
      var total = 0;
      io.glList.forEach(function (gl) {
        total += self.calculateGLTotal(gl, field);
      });
      return total;
    },
    calculateGLBudgetTotal: function (gl) {
      var total = 0;
      gl.orgList.forEach(function (org) {
        // ใช้ budgetTotal จาก backend (Initial + Added - Reduced, ไม่รวมคืนเงิน)
        total += org.budgetTotal || 0;
      });
      return total;
    },
    calculateIOBudgetTotal: function (io) {
      var self = this;
      var total = 0;
      io.glList.forEach(function (gl) {
        total += self.calculateGLBudgetTotal(gl);
      });
      return total;
    },


    // ================= CR-BudgetReport ข้อ 2: cascading filter =================

    /// เปลี่ยน filter ช่องใด -> โหลดตัวเลือกของช่องที่เหลือใหม่ (Q-C3 เรียก API ทุกครั้ง)
    onFilterChanged: function () {
      this.reloadFilterOptions();
    },

    /// โหลดตัวเลือกที่ยังเป็นไปได้ของทั้ง 4 ช่อง
    /// ตัวเลือกของช่องหนึ่งไม่ถูกกรองด้วยค่าที่เลือกในช่องนั้นเอง (ฝั่ง API จัดการให้)
    reloadFilterOptions: async function () {
      var self = this;

      if (!self.formData.year || !self.formData.cctrCode) return;

      var seq = ++self.filterOptionsSeq;

      try {
        var response = await axios.get("/Api/BudgetPlan/Create/FilterOptions", {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
          params: {
            year: self.formData.year,
            cctrCode: self.formData.cctrCode,
            ioCodes: self.searchIOCodes.join(","),
            glCodes: self.searchGLCodes.join(","),
            glTypeNames: self.searchGLTypeNames.join(","),
            orgCodes: self.searchOrgCodes.join(","),
          },
        });

        // ผู้ใช้เปลี่ยน filter ไปแล้วระหว่างรอ -> ทิ้ง response นี้
        if (seq !== self.filterOptionsSeq) return;

        if (response.data && response.data.success) {
          var d = response.data.data || {};
          self.ioTreeOptions = self.toTreeOptions(d.ios);
          self.glTreeOptions = self.toTreeOptions(d.gls);
          self.glTypeTreeOptions = self.toTreeOptions(d.glTypes);
          self.orgFilterTreeOptions = self.toTreeOptions(d.orgs);
        } else {
          // ตัวเลือกเดิมยังใช้ได้ จึงไม่ขัดจังหวะผู้ใช้ — แต่ต้องเห็นใน console
          console.warn("โหลดตัวเลือก filter ไม่สำเร็จ",
            (response.data && response.data.message) || "unknown error");
        }
      } catch (e) {
        // ไม่ขัดจังหวะผู้ใช้ — ตัวเลือกเดิมยังใช้ได้
        if (seq === self.filterOptionsSeq) {
          console.warn("โหลดตัวเลือก filter ไม่สำเร็จ", e);
        }
      }
    },

    toTreeOptions: function (list) {
      return (list || []).map(function (item) {
        return { id: item.value, label: item.text };
      });
    },

    // Search
    searchBudgetPlan: async function () {
      var self = this;

      // ไม่เลือกอะไรเลย = ดูทั้งหมด ไม่ต้องบังคับให้กรอก (filter เป็น multi-select แล้ว)
      void 0;

      try {
        KTApp.blockPage({ opacity: 0.05, message: "กำลังค้นหา..." });

        var response = await axios.get("/Api/BudgetPlan/Create/Search", {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
          params: {
            year: self.formData.year,
            cctrCode: self.formData.cctrCode, // Issue 46: ส่ง CCTR ที่เลือกจากหน้าแรก
            orgCodes: self.searchOrgCodes.join(","),
            ioCodes: self.searchIOCodes.join(","),
            glCodes: self.searchGLCodes.join(","),
            glTypeNames: self.searchGLTypeNames.join(","),
          },
        });

        KTApp.unblockPage();

        if (response.data && response.data.success && response.data.data) {
          var searchResult = response.data.data;

          if (searchResult.cctrCode) {
            // Populate form with search result
            self.formData.cctrCode = searchResult.cctrCode;
            self.formData.cctrName = searchResult.cctrName || "";
            self.formData.isFilteredView = true; // Issue 47: บันทึกจาก filtered view ห้ามลบรายการที่ถูก filter ออก

            if (searchResult.ioList && searchResult.ioList.length > 0) {
              self.formData.ioList = searchResult.ioList.map(function (io) {
                return {
                  ioCode: io.ioCode,
                  ioName: io.ioName || "",
                  collapsed: false,
                  isFromDb: true,
                  glList: (io.glList || []).map(function (gl) {
                    return {
                      glCode: gl.glCode,
                      glName: gl.glName || "",
                      glTypeName: gl.glTypeName || "",
                      collapsed: false,
                      isFromDb: true,
                      orgList: (gl.orgList || []).map(function (org) {
                        self.orgUidCounter++;
                        return {
                          _uid: self.orgUidCounter,
                          orgCode: org.orgCode,
                          orgName: org.orgName || "",
                          budgetAmount: org.budgetAmount || 0,
                          usedAmount: org.usedAmount || 0,
                          addedAmount: org.addedAmount || 0,
                          sendbackAmount: org.sendbackAmount || 0,
                          reducedAmount: org.reducedAmount || 0,
                          remainingAmount: org.remainingAmount || 0,
                          budgetTotal: org.budgetTotal || 0,
                          isReadOnly: org.usedAmount > 0 || org.addedAmount > 0 || org.sendbackAmount > 0 || org.reducedAmount > 0,
                          budgetPlanId: org.budgetPlanId,
                          // Store original codes for tracking changes
                          originalCCTRCode: searchResult.cctrCode,
                          originalIOCode: io.ioCode,
                          originalGLCode: gl.glCode,
                        };
                      }),
                    };
                  }),
                };
              });
            }
          } else {
            Swal.fire("ไม่พบข้อมูล", "ไม่พบข้อมูลที่ตรงกับเงื่อนไข", "info");
          }
        }
      } catch (error) {
        KTApp.unblockPage();
        console.error("Error searching:", error);
        Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถค้นหาข้อมูลได้", "error");
      }
    },
    clearSearch: function () {
      // Clear search filters only
      this.searchKeyword = "";
      this.searchOrgCodes = [];
      this.searchIOCodes = [];
      this.searchGLCodes = [];
      this.searchGLTypeNames = [];
      this.reloadFilterOptions();

      // In Edit/View mode: reload data from original year/cctr
      if (this.isEditMode || this.isViewMode) {
        this.loadEditData();
      }
    },

    // Save
    saveBudgetPlan: async function () {
      var self = this;

      // Validation
      if (!self.formData.year) {
        Swal.fire("กรุณากรอกข้อมูล", "กรุณาเลือกปีงบประมาณ", "warning");
        return;
      }
      if (!self.formData.cctrCode) {
        Swal.fire("กรุณากรอกข้อมูล", "กรุณากรอกรหัส CCTR", "warning");
        return;
      }
      if (!self.formData.cctrName) {
        Swal.fire("กรุณากรอกข้อมูล", "กรุณากรอกชื่อ CCTR", "warning");
        return;
      }

      // Validate IO/GL/Org
      var hasValidData = false;
      for (var i = 0; i < self.formData.ioList.length; i++) {
        var io = self.formData.ioList[i];
        if (!io.ioCode) {
          Swal.fire("กรุณากรอกข้อมูล", "กรุณากรอกรหัส IO ใน IO #" + (i + 1), "warning");
          return;
        }
        for (var j = 0; j < io.glList.length; j++) {
          var gl = io.glList[j];
          if (!gl.glCode) {
            Swal.fire("กรุณากรอกข้อมูล", "กรุณากรอกรหัส GL ใน IO #" + (i + 1) + " GL #" + (j + 1), "warning");
            return;
          }
          for (var k = 0; k < gl.orgList.length; k++) {
            var org = gl.orgList[k];
            if (!org.orgCode) {
              Swal.fire("กรุณากรอกข้อมูล", "กรุณาเลือกแผนกในรายการที่ " + (k + 1), "warning");
              return;
            }
            if (org.budgetAmount === null || org.budgetAmount === undefined || org.budgetAmount === '' || org.budgetAmount < 0) {
              Swal.fire("กรุณากรอกข้อมูล", "กรุณากรอกงบประมาณ (อนุญาตให้เป็น 0 ได้)", "warning");
              return;
            }
            hasValidData = true;
          }
        }
      }

      if (!hasValidData) {
        Swal.fire("กรุณากรอกข้อมูล", "กรุณาเพิ่มรายการแผนกและงบประมาณอย่างน้อย 1 รายการ", "warning");
        return;
      }

      // Confirm save
      var confirmResult = await Swal.fire({
        title: "ยืนยันการบันทึก",
        text: "คุณต้องการบันทึกข้อมูลแผนงบประมาณนี้หรือไม่?",
        type: "question",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
      });

      if (!confirmResult.value) {
        return;
      }

      try {
        self.isSaving = true;
        KTApp.blockPage({ opacity: 0.05, message: "กำลังบันทึกข้อมูล..." });

        var response = await axios.post(
          "/Api/BudgetPlan/Create/Save",
          self.formData,
          {
            headers: {
              Authorization: "Bearer " + self.$root.user.access_token,
            },
          }
        );

        KTApp.unblockPage();
        self.isSaving = false;

        if (response.data && response.data.success) {
          await Swal.fire({
            title: "บันทึกสำเร็จ",
            text: response.data.message || "บันทึกข้อมูลแผนงบประมาณเรียบร้อยแล้ว",
            type: "success",
          });
          self.$router.push({ path: "/BudgetPlan/List", query: { reload: Date.now() } });
        } else {
          Swal.fire("เกิดข้อผิดพลาด", response.data.message || "ไม่สามารถบันทึกข้อมูลได้", "error");
        }
      } catch (error) {
        KTApp.unblockPage();
        self.isSaving = false;
        console.error("Error saving:", error);
        var message = "ไม่สามารถบันทึกข้อมูลได้";
        if (error.response && error.response.data && error.response.data.message) {
          message = error.response.data.message;
        }
        Swal.fire("เกิดข้อผิดพลาด", message, "error");
      }
    },

    // Check if GL can be deleted (no org has been used)
    canDeleteGL: function (gl) {
      // Can delete if no org list or all orgs are not readonly
      if (!gl.orgList || gl.orgList.length === 0) {
        return true;
      }
      return gl.orgList.every(function (org) {
        return !org.isReadOnly;
      });
    },

    // Check if IO can be deleted (no GL has been used)
    canDeleteIO: function (io) {
      var self = this;
      // Can delete if no GL list or all GLs can be deleted
      if (!io.glList || io.glList.length === 0) {
        return true;
      }
      return io.glList.every(function (gl) {
        return self.canDeleteGL(gl);
      });
    },

    // Format Number
    formatNumber: function (value) {
      if (value === null || value === undefined) return "0.00";
      return parseFloat(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // Export Budget History to Excel
    exportBudgetHistory: async function () {
      var self = this;
      var year = self.formData.year;
      var cctrCode = self.formData.cctrCode;

      if (!year || !cctrCode) {
        Swal.fire("ไม่สามารถ Export ได้", "กรุณาเลือกข้อมูลงบประมาณก่อน", "warning");
        return;
      }

      try {
        self.isExporting = true;

        // Build query parameters
        var params = new URLSearchParams();
        params.append("year", year);
        params.append("cctrCode", cctrCode);

        // Add optional filters from search
        if (self.searchOrgCodes && self.searchOrgCodes.length > 0) {
          params.append("orgCodes", self.searchOrgCodes.join(","));
        }
        // CR-BudgetReport ข้อ 2 (UC-02): ไฟล์ที่ export ต้องสะท้อน filter ที่เลือกบนหน้าจอ
        if (self.searchIOCodes && self.searchIOCodes.length > 0) {
          params.append("ioCodes", self.searchIOCodes.join(","));
        }
        if (self.searchGLCodes && self.searchGLCodes.length > 0) {
          params.append("glCodes", self.searchGLCodes.join(","));
        }
        if (self.searchGLTypeNames && self.searchGLTypeNames.length > 0) {
          params.append("glTypeNames", self.searchGLTypeNames.join(","));
        }

        var response = await axios.get("/Api/BudgetPlan/ExportHistory?" + params.toString(), {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
          responseType: "blob",
        });

        self.isExporting = false;

        // Get filename from Content-Disposition header or use default
        var filename = "BudgetHistory_" + year + "_" + cctrCode + ".xlsx";
        var contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          var filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ""));
          }
        }

        // Create download link
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        var link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(link.href);

        Swal.fire("สำเร็จ", "Export ข้อมูลงบประมาณเรียบร้อยแล้ว", "success");
      } catch (error) {
        self.isExporting = false;
        console.error("Error exporting budget history:", error);

        var message = "ไม่สามารถ Export ข้อมูลได้";
        if (error.response && error.response.status === 404) {
          message = "ไม่พบข้อมูลสำหรับ Export";
        } else if (error.response && error.response.data && error.response.data.message) {
          message = error.response.data.message;
        }

        Swal.fire("เกิดข้อผิดพลาด", message, "error");
      }
    },
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.card-header {
  padding: 0.75rem 1rem;
}
.card-header h5,
.card-header h6 {
  margin-bottom: 0;
}
.ml-3 {
  margin-left: 1.5rem !important;
}
.table-sm td,
.table-sm th {
  padding: 0.4rem;
  vertical-align: middle;
}
.badge {
  font-size: 0.85em;
}
.readonly-field {
  background-color: #e9ecef !important;
  cursor: not-allowed;
}
.summary-box {
  font-size: 0.9rem;
  margin-top: 1.5rem;
}
.summary-box .d-flex {
  padding: 0.25rem 0;
}
</style>



// WEBPACK FOOTER //
// App/Components/BudgetPlan/BudgetPlanCreate.vue